import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { PageError } from '@/components/ui/PageAsyncState'
import { ProtoExcelTable, type ProtoColumn } from '@/components/table/ProtoExcelTable'
import { computeExplosionListKpis } from '@/features/explosions/explosionListMetrics'
import {
  explosionService,
  type ExplosionRun,
} from '@/services/explosions'

type RunRow = ExplosionRun & { result_count: number }

export function ExplosionRunListPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<RunRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function reload() {
    setLoading(true)
    setError(null)
    try {
      const runs = await explosionService.list()
      const withCounts = await Promise.all(
        runs.map(async (r) => {
          const detail = await explosionService.getById(r.id)
          return {
            ...r,
            result_count: detail?.results.length ?? 0,
          }
        }),
      )
      setRows(withCounts)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Không tải được danh sách explosion',
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  const kpis = useMemo(() => computeExplosionListKpis(rows), [rows])

  const columns: ProtoColumn<RunRow>[] = useMemo(
    () => [
      {
        id: 'ran_at',
        header: 'Thời điểm',
        sortable: true,
        hideable: false,
        cell: (r) => new Date(r.ran_at).toLocaleString('vi-VN'),
        sortValue: (r) => r.ran_at,
      },
      {
        id: 'ran_by',
        header: 'Người chạy',
        cell: (r) => r.ran_by || '—',
        sortValue: (r) => r.ran_by ?? '',
      },
      {
        id: 'result_count',
        header: 'Số dòng KQ',
        sortable: true,
        cell: (r) => r.result_count,
        sortValue: (r) => r.result_count,
      },
      {
        id: 'id',
        header: 'Run ID',
        cell: (r) => (
          <span className="font-mono text-xs text-slate-600">{r.id}</span>
        ),
        sortValue: (r) => r.id,
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Kế hoạch', to: '/planning' },
          { label: 'Gross Explosion' },
        ]}
      />
      <PageHeader
        title="Gross Explosion"
        subtitle="Snapshot bất biến — chạy từ trang Nhu cầu vật tư"
      />

      <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
        <KpiCard label="Số lần chạy" value={kpis.runs} />
        <KpiCard label="Tổng dòng KQ" value={kpis.resultCountSum} />
      </div>

      {error ? <PageError message={error} onRetry={() => void reload()} /> : null}

      <ProtoExcelTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        searchPlaceholder="Tìm run…"
        exportName="gross-explosion-runs"
        pageSize={50}
        loading={loading}
        emptyMessage="Chưa có lần chạy nào."
        showRowNumbers
        stickyLeftColumnIds={['ran_at']}
        onRowClick={(r) => navigate(`/purchasing/explosions/${r.id}`)}
      />
    </div>
  )
}
